import { useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';

import { color } from '@creditornot/cb-ingredients/design-tokens';

import { TIME_PER_STEP } from '../constants';

const Root = styled.div`
  display: flex;
  gap: 4px;
`;

const Step = styled.div`
  height: 4px;
  background: #ffffff3d;
  border-radius: 2px;
  flex: 1;
  cursor: pointer;
`;

const StepProgress = styled.div<{
  $isInitialRender: boolean;
  $state: 'seen' | 'current' | 'unseen';
  $isPaused: boolean;
  $progressBeforePausePercentage: number | null;
  $timeUntilStepChange: number;
}>`
  height: 100%;
  width: ${({ $state, $isInitialRender, $progressBeforePausePercentage, $isPaused }) =>
    $state === 'unseen' || $isInitialRender
      ? 0
      : $state === 'current' && $isPaused
        ? $progressBeforePausePercentage
        : 100}%;
  ${({ $state, $timeUntilStepChange, $isPaused }) =>
    $state === 'current' && !$isPaused && `transition: ${$timeUntilStepChange}ms linear`};
  background-color: ${color.bg};
  border-radius: 2px;
`;

const getStepState = (index: number, currentStep: number, totalSteps: number) => {
  if (index === totalSteps - 1 && index === currentStep) {
    return 'seen';
  } else if (index === currentStep) {
    return 'current';
  } else if (index < currentStep) {
    return 'seen';
  } else {
    return 'unseen';
  }
};

export const Steps: React.FC<{
  currentStep: number;
  totalSteps: number;
  isPaused: boolean;
  progressBeforePause: number;
  onStepClick: (step: number) => void;
}> = ({ currentStep, totalSteps, isPaused, progressBeforePause, onStepClick }) => {
  const [isInitialRender, setInitialRender] = useState(true);

  useEffect(() => {
    setInitialRender(false);
  }, []);

  const allSteps = useMemo(() => Array.from(Array(totalSteps).keys()), [totalSteps]);

  const progressBeforePausePercentage = useMemo(
    () => (progressBeforePause / TIME_PER_STEP) * 100,
    [progressBeforePause],
  );

  const timeUntilStepChange = useMemo(
    () => ((100 - progressBeforePausePercentage) / 100) * TIME_PER_STEP,
    [progressBeforePausePercentage],
  );

  return (
    <Root>
      {allSteps.map((step, index) => (
        <Step
          key={step}
          onClick={() => {
            onStepClick(index);
          }}
        >
          <StepProgress
            $state={getStepState(index, currentStep, totalSteps)}
            $isInitialRender={isInitialRender}
            $progressBeforePausePercentage={progressBeforePausePercentage}
            $timeUntilStepChange={timeUntilStepChange}
            $isPaused={isPaused}
          />
        </Step>
      ))}
    </Root>
  );
};
